/**
 * Webtech HR Engine™ - Page de connexion
 * Design moderne avec animations et meilleure UX
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import TurnstileWidget from '../components/TurnstileWidget';
import { toast } from 'sonner';
import { 
  Eye, 
  EyeOff, 
  Loader2, 
  Sparkles, 
  ArrowRight,
  Users,
  BarChart3,
  Shield,
  CheckCircle2
} from 'lucide-react';

export default function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for verified param from registration or session expiry
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === 'true') {
      toast.success('Compte vérifié ! Vous pouvez maintenant vous connecter.');
    }
    if (params.get('expired') === 'true') {
      toast.info('Votre session a expiré pour inactivité. Veuillez vous reconnecter.');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!identifier.trim()) {
      toast.error('Veuillez entrer votre identifiant (email, téléphone ou matricule)');
      return;
    }
    
    if (!password) {
      toast.error('Veuillez entrer votre mot de passe');
      return;
    }

    setLoading(true);

    try {
      // Turnstile token is optional on login (server controls enforcement via env)
      await login(identifier, password, turnstileToken);
      toast.success('Connexion réussie ! Bienvenue 👋');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Erreur de connexion';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (!error.response) {
        errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
      }
      
      toast.error(errorMessage);
      // Reset captcha widget (single-use) if it was used
      try { turnstileRef.current?.reset(); } catch (_) {}
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Users, text: 'Gestion de 500+ employés' },
    { icon: BarChart3, text: 'Analyses en temps réel' },
    { icon: Shield, text: 'Données 100% sécurisées' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="w-full max-w-md">
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 group">
              <div className="w-14 h-14 bg-gradient-to-br from-webtech-blue to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-all duration-300">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div className="text-left">
                <span className="block text-xl font-bold text-slate-900">HR Engine</span>
                <span className="block text-xs text-slate-500">by Webtech</span>
              </div>
            </Link>
          </div>

          {/* Welcome Card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Bon retour ! 👋
              </h1>
              <p className="text-slate-500">
                Connectez-vous pour accéder à votre espace
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="identifier" className="text-slate-700 font-medium">
                  Email, Téléphone ou Matricule
                </Label>
                <Input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="email@exemple.com, +223 XX XX XX XX ou MAT001"
                  required
                  className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-webtech-blue focus:ring-2 focus:ring-webtech-blue/20 transition-all"
                  data-testid="login-identifier-input"
                />
                <p className="text-xs text-slate-500">
                  Connectez-vous avec votre email, numéro de téléphone ou matricule
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700 font-medium">
                    Mot de passe
                  </Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-webtech-blue hover:text-webtech-blue-hover font-medium transition-colors"
                  >
                    Oublié ?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="h-12 pr-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-webtech-blue focus:ring-2 focus:ring-webtech-blue/20 transition-all"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-all"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-webtech-blue focus:ring-webtech-blue"
                />
                <label htmlFor="remember-me" className="ml-2 text-sm text-slate-600">
                  Se souvenir de moi
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-webtech-blue to-blue-600 hover:from-webtech-blue-hover hover:to-blue-700 text-white text-base font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Connexion en cours...
                  </>
                ) : (
                  <>
                    Se connecter
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-4 text-slate-400">Pas encore de compte ?</span>
              </div>
            </div>

            {/* Self-signup retired: prospects must request a demo via the landing page; the admin creates accounts manually */}
            <Link to="/#pricing" data-testid="cta-request-demo">
              <Button
                variant="outline"
                className="w-full h-11 border-2 border-slate-200 hover:border-webtech-blue hover:bg-webtech-blue/5 text-slate-700 hover:text-webtech-blue font-medium transition-all"
              >
                Demander une démo / un devis
              </Button>
            </Link>
          </div>

          {/* Back to home */}
          <p className="text-center mt-6 text-sm text-slate-500">
            <Link to="/" className="inline-flex items-center gap-1 text-webtech-blue hover:text-webtech-blue-hover font-medium transition-colors">
              ← Retour à l'accueil
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image & Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-webtech-blue via-blue-600 to-indigo-700 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>

        {/* Content */}
        <div className="relative flex flex-col justify-center px-12 py-16 w-full">
          {/* Main headline */}
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
              Gérez vos talents<br />avec intelligence
            </h2>
            <p className="text-blue-100 text-lg">
              La plateforme RH nouvelle génération pour les entreprises africaines
            </p>
          </div>

          {/* Features list */}
          <div className="space-y-4 mb-12">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
              >
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <feature.icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-medium">{feature.text}</span>
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                AK
              </div>
              <div>
                <p className="text-white/90 text-sm mb-3 italic">
                  "Webtech HR Engine a transformé notre façon de gérer nos équipes. 
                  L'organigramme dynamique est un vrai gain de temps au quotidien."
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold text-sm">Amadou K.</span>
                  <span className="text-white/60 text-sm">• Directeur des Ressources Humaines</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
